import type { ColumnDef } from "@tanstack/react-table";

import type { BaseRowItem } from "../../components/data-table";
import type { PointsClaimRequest } from "../../lib/api/pointsClaimRequests";
import { formatAmount } from "../../utils/FormatPerson";
import { statusBadgeClass } from "../orders/ordersOrderTable";

function formatClaimDate(iso: string | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const day = date.getDate();
  const ord =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
          ? "rd"
          : "th";
  return `${date.toLocaleString("default", { month: "short" })} ${day}${ord}, ${date.getFullYear()}, ${date.toLocaleString("default", { hour: "numeric", minute: "numeric", hour12: true })}`;
}

function userDisplayName(r: PointsClaimRequest): string {
  const p = r.user?.profile;
  const f = p?.firstName?.trim() ?? "";
  const l = p?.lastName?.trim() ?? "";
  const name = `${f} ${l}`.trim();
  if (name) return name;
  const u = p?.username?.trim();
  if (u) return `@${u}`;
  return "—";
}

function statusPillClass(status: string): string {
  const u = status.toUpperCase();
  const mapped =
    u === "APPROVED"
      ? "COMPLETED"
      : u === "DECLINED"
        ? "FAILED"
        : u === "PENDING"
          ? "PENDING"
          : u;
  return `inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${statusBadgeClass(mapped)}`;
}

export type PointsClaimTableRow = BaseRowItem & {
  raw: PointsClaimRequest;
  displayDate: string;
  userLabel: string;
  emailHint: string;
  pointsLabel: string;
  ngnLabel: string;
  sourceLabel: string;
  statusLabel: string;
};

export function buildPointsClaimTableRows(
  items: PointsClaimRequest[]
): PointsClaimTableRow[] {
  return items.map((r) => ({
    id: r._id,
    raw: r,
    displayDate: formatClaimDate(r.createdAt ?? r.updatedAt),
    userLabel: userDisplayName(r),
    emailHint: r.user?.email?.address?.trim() || "—",
    pointsLabel: formatAmount(r.points),
    ngnLabel: `₦${formatAmount(r.cashAmount)}`,
    sourceLabel: r.source?.replace(/_/g, " ") || "—",
    statusLabel: r.status || "—",
  }));
}

export function createPointsClaimTableColumns(options: {
  onView: (row: PointsClaimTableRow) => void;
  onApprove: (row: PointsClaimTableRow) => void;
  onDecline: (row: PointsClaimTableRow) => void;
  actingId: string | null;
}): ColumnDef<PointsClaimTableRow, unknown>[] {
  const { onView, onApprove, onDecline, actingId } = options;

  return [
    {
      accessorKey: "displayDate",
      header: "Requested",
      meta: { hideBelow: "lg" as const },
      cell: ({ getValue }) => (
        <span className="text-adminTable-muted text-[13px]">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "userLabel",
      header: "User",
      cell: ({ row }) => (
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-[14px] font-semibold text-white">
            {row.original.userLabel}
          </span>
          <span className="truncate text-[11px] text-adminTable-muted">
            {row.original.emailHint}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "sourceLabel",
      header: "Source",
      meta: { hideBelow: "md" as const },
      cell: ({ getValue }) => (
        <span className="text-[12px] font-medium uppercase tracking-wide text-[#BEE1FB]">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "pointsLabel",
      header: "Points",
      cell: ({ getValue }) => (
        <span className="tabular-nums text-white">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "ngnLabel",
      header: "NGN",
      cell: ({ getValue }) => (
        <span className="tabular-nums text-white">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "statusLabel",
      header: "Status",
      cell: ({ row }) => (
        <span className={statusPillClass(row.original.raw.status)}>
          {row.original.statusLabel}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      meta: { align: "right" as const },
      cell: ({ row }) => {
        const pending = row.original.raw.status?.toUpperCase() === "PENDING";
        const busy = actingId === row.original.raw._id;
        return (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onView(row.original);
              }}
              className="rounded-lg border border-[#3A4852] bg-[#1C2227] px-3 py-1.5 text-[12px] font-semibold text-[#78C2F7] transition hover:border-secondary_button hover:bg-[#273037]"
            >
              View
            </button>
            {pending ? (
              <>
                <button
                  type="button"
                  disabled={Boolean(actingId)}
                  onClick={(e) => {
                    e.stopPropagation();
                    onApprove(row.original);
                  }}
                  className="rounded-lg bg-[#0E2B1F] px-3 py-1.5 text-[12px] font-semibold text-[#03C668] transition hover:opacity-90 disabled:opacity-40"
                >
                  {busy ? "…" : "Approve"}
                </button>
                <button
                  type="button"
                  disabled={Boolean(actingId)}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDecline(row.original);
                  }}
                  className="rounded-lg bg-[#2A1518] px-3 py-1.5 text-[12px] font-semibold text-[#DF4E4E] transition hover:opacity-90 disabled:opacity-40"
                >
                  Decline
                </button>
              </>
            ) : null}
          </div>
        );
      },
    },
  ];
}
